import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Papa from "papaparse";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  addMinutes,
  addDays,
  addYears,
} from "date-fns";
import { fr } from "date-fns/locale";
import { motion } from "framer-motion";
import { CalendarDays, Users, Save, Check, AlertCircle, Filter, ChevronDown, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import { toast } from "react-hot-toast";

const typeColors = {
  "Consommation Partagée": "#F8BD3D",
  "Injection Partagée": "#22C55E",
  "Consommation Réseau": "#64748B",
  "Injection Réseau": "#6B7280",
};

function parseDate(dateStr) {
  if (!dateStr) {
    console.warn('Date vide ou nulle détectée');
    return null;
  }

  try {
    const parts = dateStr.trim().split(" ");
    if (!parts[0]) {
      console.warn(`Format de date invalide: "${dateStr}"`);
      return null;
    }

    const dateParts = parts[0].split("/");
    if (dateParts.length !== 3) {
      console.warn(`Format de date invalide (pas 3 parties): "${dateStr}"`);
      return null;
    }

    const [day, month, year] = dateParts;

    if (!day || !month || !year) {
      console.warn(`Composants de date manquants: "${dateStr}"`);
      return null;
    }

    const time = parts[1] || "00:00";
    const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${time}`;
    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      console.warn(`Date invalide après parsing: "${dateStr}" -> "${isoString}"`);
      return null;
    }

    return date;
  } catch (err) {
    console.error('Erreur lors du parsing de date:', dateStr, err);
    toast.error(`Date invalide détectée: ${dateStr}`);
    return null;
  }
}

function normalizeType(type) {
  if (!type) return "Inconnu";
  return type.trim();
}

export default function AdminEanChart({ csvUrl }) {
  const [data, setData] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState(["Tous"]);
  const [granularity, setGranularity] = useState("day");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([
    "Consommation Partagée",
    "Injection Partagée",
    "Consommation Réseau",
    "Injection Réseau",
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [invalidDatesCount, setInvalidDatesCount] = useState(0);
  const [showParticipantFilter, setShowParticipantFilter] = useState(false);

  const eanToNameRef = useRef({});
  const filterRef = useRef(null);
  const loadedCsvUrlRef = useRef(null);

  useEffect(() => {
    const loadParticipants = async () => {
      try {
        const { data: participantData, error } = await supabase
          .from('participants')
          .select('ean_code, name');

        if (error) {
          console.error('Erreur chargement participants:', error);
          return;
        }

        const mapping = {};
        participantData?.forEach(p => {
          if (p.ean_code && p.name) {
            mapping[p.ean_code] = p.name;
          }
        });

        eanToNameRef.current = mapping;
      } catch (err) {
        console.error('Erreur lors du chargement des participants:', err);
      }
    };

    loadParticipants();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowParticipantFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!csvUrl) {
      setData([]);
      setParticipants([]);
      loadedCsvUrlRef.current = null;
      return;
    }

    if (loadedCsvUrlRef.current === csvUrl) {
      return;
    }

    const loadCsvFilesSequentially = async () => {
      setLoading(true);
      setInvalidDatesCount(0);

      const urls = csvUrl.split(',').map(url => url.trim()).filter(url => url);

      if (urls.length === 0) {
        setLoading(false);
        return;
      }

      const allRows = [];
      let totalInvalidDates = 0;

      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];

        try {
          const response = await fetch(url, {
            method: 'GET',
            redirect: 'follow',
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const csvText = await response.text();

          const result = await new Promise((resolve, reject) => {
            Papa.parse(csvText, {
              header: true,
              skipEmptyLines: true,
              complete: resolve,
              error: reject,
            });
          });

          let invalidInFile = 0;

          const rows = result.data
            .map((r, idx) => {
              if (!r["Date Début"] || !r["EAN"] || !r["Type de volume"]) {
                return null;
              }

              const parsedDate = parseDate(r["Date Début"]);

              if (!parsedDate) {
                invalidInFile++;
                return null;
              }

              const volumeStr = r["Volume (kWh)"];
              const normalizedVolume = volumeStr ? volumeStr.toString().replace(',', '.') : '0';
              const volume = parseFloat(normalizedVolume);
              if (isNaN(volume)) {
                console.warn(`Volume invalide à la ligne ${idx}: ${r["Volume (kWh)"]}`);
              }

              const normalizedType = normalizeType(r["Type de volume"]);
              if (normalizedType === null) {
                return null;
              }

              return {
                date: parsedDate,
                ean: r["EAN"].trim(),
                name: eanToNameRef.current[r["EAN"]] || r["EAN"],
                type: normalizedType,
                volume: isNaN(volume) ? 0 : volume,
              };
            })
            .filter(row => row !== null);

          if (invalidInFile > 0) {
            console.warn(`${invalidInFile} dates invalides dans le fichier ${i + 1}/${urls.length}`);
            totalInvalidDates += invalidInFile;
          }

          for (let j = 0; j < rows.length; j++) {
            allRows.push(rows[j]);
          }

        } catch (err) {
          console.error(`Erreur fichier ${i + 1}/${urls.length}:`, err);
          toast.error(`Erreur fichier ${i + 1}: ${err.message}`);
        }
      }

      if (totalInvalidDates > 0) {
        setInvalidDatesCount(totalInvalidDates);
        toast.error(`${totalInvalidDates} lignes avec dates invalides ignorées`, {
          duration: 4000,
          icon: <AlertCircle className="w-5 h-5 text-orange-500" />,
        });
      }

      const validRows = allRows.filter(r => {
        if (!r || !r.date || isNaN(r.date.getTime())) {
          console.warn('Ligne avec date invalide filtrée:', r);
          return false;
        }
        return true;
      });

      validRows.sort((a, b) => a.date.getTime() - b.date.getTime());

      const uniqueNames = [...new Set(validRows.map((r) => r.name))];
      setParticipants(["Tous", ...uniqueNames.sort()]);
      setData(validRows);
      loadedCsvUrlRef.current = csvUrl;
      setLoading(false);
    };

    loadCsvFilesSequentially();
  }, [csvUrl]);

  const handleSaveLink = async () => {
    if (!csvUrl) {
      toast.error('Aucun lien à sauvegarder');
      return;
    }

    if (data.length === 0) {
      toast.error('Aucune donnée disponible');
      return;
    }

    setIsSaving(true);
    try {
      const validDates = data
        .map(d => d.date)
        .filter(d => d && !isNaN(d.getTime()));

      if (validDates.length === 0) {
        toast.error('Aucune date valide');
        setIsSaving(false);
        return;
      }

      validDates.sort((a, b) => a - b);
      const medianDate = validDates[Math.floor(validDates.length / 2)];

      const year = medianDate.getFullYear();
      const monthNumber = medianDate.getMonth() + 1;
      const monthName = format(medianDate, 'MMMM yyyy', { locale: fr });

      const { data: existing, error: checkError } = await supabase
        .from('monthly_csv_links')
        .select('id')
        .eq('year', year)
        .eq('month_number', monthNumber)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existing) {
        const { error: updateError } = await supabase
          .from('monthly_csv_links')
          .update({
            csv_url: csvUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (updateError) throw updateError;
        toast.success('Lien mis à jour avec succès!');
      } else {
        const { error: insertError } = await supabase
          .from('monthly_csv_links')
          .insert({
            month_name: monthName,
            csv_url: csvUrl,
            year: year,
            month_number: monthNumber
          });

        if (insertError) throw insertError;
        toast.success('Lien sauvegardé avec succès!');
      }

      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePeriodShift = useCallback((direction) => {
    setSelectedDate(prevDate => {
      if (granularity === "day") return addDays(prevDate, direction);
      if (granularity === "week") return addDays(prevDate, 7 * direction);
      if (granularity === "year") return addYears(prevDate, direction);
      return prevDate;
    });
  }, [granularity]);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    try {
      let filtered = selectedParticipants.includes("Tous")
        ? data
        : data.filter((d) => selectedParticipants.includes(d.name));

      filtered = filtered.filter((d) => selectedTypes.includes(d.type));

      let start, end;

      try {
        if (granularity === "day") {
          start = startOfDay(selectedDate);
          end = endOfDay(selectedDate);
        } else if (granularity === "week") {
          start = startOfWeek(selectedDate, { weekStartsOn: 1 });
          end = endOfWeek(selectedDate, { weekStartsOn: 1 });
        } else if (granularity === "year") {
          start = startOfYear(selectedDate);
          end = endOfYear(selectedDate);
        } else {
          console.error('Granularité invalide:', granularity);
          return [];
        }
      } catch (err) {
        console.error('Erreur calcul dates start/end:', err);
        return [];
      }

      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.error('Dates de période invalides:', { start, end, granularity });
        return [];
      }

      filtered = filtered.filter((d) => {
        if (!d.date || isNaN(d.date.getTime())) {
          return false;
        }
        return d.date >= start && d.date <= end;
      });

      return filtered;
    } catch (err) {
      console.error('Erreur filtrage données:', err);
      return [];
    }
  }, [data, selectedParticipants, selectedTypes, granularity, selectedDate]);

  const chartData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return [];
    }

    try {
      let start, end;

      if (granularity === "day") {
        start = startOfDay(selectedDate);
        end = endOfDay(selectedDate);
      } else if (granularity === "week") {
        start = startOfWeek(selectedDate, { weekStartsOn: 1 });
        end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      } else if (granularity === "year") {
        start = startOfYear(selectedDate);
        end = endOfYear(selectedDate);
      } else {
        return [];
      }

      const grouped = [];
      const processedDataPoints = new Set();

      if (granularity === "year") {
        for (let month = 0; month < 12; month++) {
          const monthStart = new Date(selectedDate.getFullYear(), month, 1);
          const monthEnd = new Date(selectedDate.getFullYear(), month + 1, 0, 23, 59, 59, 999);

          const label = format(monthStart, "MMM", { locale: fr });
          const obj = { name: label };

          for (const type of selectedTypes) {
            const volumeSum = filteredData
              .filter((d) => {
                if (d.type !== type) return false;
                const dateTime = d.date.getTime();
                return dateTime >= monthStart.getTime() && dateTime <= monthEnd.getTime();
              })
              .reduce((sum, d, idx) => {
                const key = `${month}-${type}-${idx}`;
                if (!processedDataPoints.has(key)) {
                  processedDataPoints.add(key);
                  return sum + (d.volume || 0);
                }
                return sum;
              }, 0);

            obj[type] = volumeSum;
          }

          grouped.push(obj);
        }
      } else {
        const step = granularity === "day" ? 15 : 24 * 60;
        const maxIterations = granularity === "day" ? 96 : 7;

        let current = new Date(start.getTime());
        let iterations = 0;

        while (current.getTime() <= end.getTime() && iterations < maxIterations) {
          const next = addMinutes(current, step);
          const currentTime = current.getTime();
          const nextTime = next.getTime();

          let label;
          try {
            label = granularity === "day"
              ? format(current, "HH:mm")
              : format(current, "EEE dd/MM", { locale: fr });
          } catch (err) {
            console.error('Erreur formatage label:', err);
            label = `Point ${iterations}`;
          }

          const obj = { name: label };

          for (const type of selectedTypes) {
            const volumeSum = filteredData
              .filter((d) => {
                if (!d.date || isNaN(d.date.getTime()) || d.type !== type) return false;
                const dateTime = d.date.getTime();
                return dateTime >= currentTime && dateTime < nextTime;
              })
              .reduce((sum, d) => sum + (d.volume || 0), 0);

            obj[type] = volumeSum;
          }

          grouped.push(obj);
          current = next;
          iterations++;
        }

        if (iterations >= maxIterations) {
          console.warn(`Limite d'itérations atteinte: ${maxIterations}`);
        }
      }

      return grouped;
    } catch (err) {
      console.error('Erreur dans useMemo chartData:', err);
      toast.error('Erreur lors du calcul des données du graphique');
      return [];
    }
  }, [filteredData, selectedTypes, granularity, selectedDate]);

  const totalsByType = useMemo(() => {
    const totals = {};

    selectedTypes.forEach((type) => {
      const typeTotal = filteredData
        .filter(d => d.type === type)
        .reduce((sum, d) => sum + (d.volume || 0), 0);

      totals[type] = typeTotal;
    });

    const chartTotal = {};
    selectedTypes.forEach((type) => {
      chartTotal[type] = chartData.reduce((sum, d) => sum + (d[type] || 0), 0);
    });

    const hasDiscrepancy = selectedTypes.some(type => {
      const diff = Math.abs(totals[type] - chartTotal[type]);
      return diff > 0.01;
    });

    if (hasDiscrepancy) {
      console.warn('Écart détecté entre totaux source et graphique:', {
        source: totals,
        chart: chartTotal
      });
    }

    return totals;
  }, [filteredData, chartData, selectedTypes]);

  const totalVolume = useMemo(() => {
    return Object.values(totalsByType).reduce((sum, val) => sum + val, 0);
  }, [totalsByType]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700">{entry.name}:</span>
            <span className="font-semibold text-gray-900">
              {(entry.value || 0).toFixed(2)} kWh
            </span>
          </div>
        ))}
      </div>
    );
  };

  const numberOfFiles = useMemo(() => {
    return csvUrl ? csvUrl.split(',').filter(url => url.trim()).length : 0;
  }, [csvUrl]);

  const dateRange = useMemo(() => {
    if (data.length === 0) return null;

    try {
      const validDates = data
        .map(d => d.date)
        .filter(d => d && !isNaN(d.getTime()))
        .map(d => d.getTime());

      if (validDates.length === 0) return null;

      return {
        start: new Date(Math.min(...validDates)),
        end: new Date(Math.max(...validDates))
      };
    } catch (err) {
      console.error('Erreur calcul dateRange:', err);
      return null;
    }
  }, [data]);

  return (
    <div className="mt-10 bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <h3 className="text-2xl font-semibold flex items-center gap-2">
            <Users className="w-6 h-6" style={{ color: '#F8BD3D' }} />
            Tableau énergétique
          </h3>
          {numberOfFiles > 1 && dateRange && (
            <p className="text-sm text-gray-500 mt-1">
              {numberOfFiles} fichiers combinés • {format(dateRange.start, 'MMMM yyyy', { locale: fr })} - {format(dateRange.end, 'MMMM yyyy', { locale: fr })}
            </p>
          )}
          {invalidDatesCount > 0 && (
            <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {invalidDatesCount} lignes avec dates invalides ignorées
            </p>
          )}
        </div>
        <div className="text-gray-500 text-sm">
          <div className="text-right">
            {selectedTypes.map((type) => (
              <div key={type} className="mb-1">
                <span className="text-gray-600">{type}:</span>{" "}
                <span
                  className="font-semibold ml-1"
                  style={{ color: typeColors[type] }}
                >
                  {(totalsByType[type] || 0).toFixed(2)} kWh
                </span>
              </div>
            ))}
            <div className="border-t border-gray-300 mt-2 pt-2">
              <span className="text-gray-600">Total:</span>{" "}
              <span className="font-semibold text-gray-900 ml-1">
                {totalVolume.toFixed(2)} kWh
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {!csvUrl ? (
        <p className="text-gray-500 italic">Aucun fichier sélectionné</p>
      ) : loading ? (
        <div className="flex items-center gap-3 text-gray-500 italic">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span>Chargement des données...</span>
        </div>
      ) : (
        <>
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-900">Filtrer par participants</span>
                </div>
                <button
                  onClick={() => setSelectedParticipants(["Tous"])}
                  className="text-sm font-medium"
                  style={{ color: '#F8BD3D' }}
                >
                  Tout afficher
                </button>
              </div>

              {!selectedParticipants.includes("Tous") && selectedParticipants.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedParticipants.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
                      style={{ backgroundColor: '#FFF5E1', color: '#1a1a1a' }}
                    >
                      {p}
                      <button
                        onClick={() => {
                          const newSelection = selectedParticipants.filter(s => s !== p);
                          setSelectedParticipants(newSelection.length > 0 ? newSelection : ["Tous"]);
                        }}
                        className="ml-1 rounded-full p-0.5"
                        style={{ backgroundColor: 'rgba(248, 189, 61, 0.3)' }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => setShowParticipantFilter(!showParticipantFilter)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                >
                  <span className="text-gray-700">
                    {selectedParticipants.includes("Tous")
                      ? `Tous les participants (${participants.length - 1})`
                      : `${selectedParticipants.length} participant(s) sélectionné(s)`}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${showParticipantFilter ? 'rotate-180' : ''}`} />
                </button>

                {showParticipantFilter && (
                  <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                      <button
                        onClick={() => {
                          setSelectedParticipants(["Tous"]);
                          setShowParticipantFilter(false);
                        }}
                        className="w-full text-left px-3 py-2 rounded-md transition-colors hover:bg-gray-100"
                        style={selectedParticipants.includes("Tous") ? { backgroundColor: '#FFF5E1', color: '#1a1a1a', fontWeight: 500 } : {}}
                      >
                        Tous les participants
                      </button>
                    </div>
                    <div className="p-2">
                      {participants.filter(p => p !== "Tous").map((p) => (
                        <label
                          key={p}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedParticipants.includes(p)}
                            onChange={(e) => {
                              let newSelection;
                              if (e.target.checked) {
                                newSelection = [...selectedParticipants.filter(s => s !== "Tous"), p];
                              } else {
                                newSelection = selectedParticipants.filter(s => s !== p);
                              }
                              setSelectedParticipants(newSelection.length > 0 ? newSelection : ["Tous"]);
                            }}
                            className="w-4 h-4 rounded border-gray-300"
                            style={{ accentColor: '#F8BD3D' }}
                          />
                          <span className="text-sm text-gray-700">{p}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

          <div className="flex flex-wrap items-center gap-4">

            <div>
              <label className="block text-sm font-medium mb-1">Granularité</label>
              <select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="border rounded-md px-2 py-1"
              >
                <option value="day">Jour (quart d'heure)</option>
                <option value="week">Semaine</option>
                <option value="year">Année</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <CalendarDays className="w-4 h-4" /> Date de référence
              </label>
              <input
                type="date"
                value={format(selectedDate, "yyyy-MM-dd")}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                className="border rounded-md px-2 py-1"
              />
            </div>

            <div className="flex gap-2 items-end">
              <button
                onClick={() => handlePeriodShift(-1)}
                className="px-3 py-1 border rounded-md hover:bg-gray-100"
              >
                ⬅️
              </button>
              <button
                onClick={() => handlePeriodShift(1)}
                className="px-3 py-1 border rounded-md hover:bg-gray-100"
              >
                ➡️
              </button>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleSaveLink}
                disabled={isSaving || !csvUrl}
                className="flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                style={{ backgroundColor: isSaved ? '#22C55E' : '#F8BD3D', color: isSaved ? '#fff' : '#1a1a1a' }}
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Sauvegarde...</span>
                  </>
                ) : isSaved ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Sauvegardé</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Sauvegarder le lien</span>
                  </>
                )}
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Types</label>
              <div className="flex flex-wrap gap-3">
                {Object.keys(typeColors).map((type) => (
                  <label key={type} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type)}
                      onChange={() =>
                        setSelectedTypes((prev) =>
                          prev.includes(type)
                            ? prev.filter((t) => t !== type)
                            : [...prev, type]
                        )
                      }
                    />
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: typeColors[type], color: "#fff" }}
                    >
                      {type}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          </div>

          <div className="w-full h-[450px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 20, right: 40, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {selectedTypes.map((type) => (
                  <Area
                    key={type}
                    type="monotone"
                    dataKey={type}
                    stroke={typeColors[type]}
                    strokeWidth={2}
                    fill={typeColors[type]}
                    fillOpacity={0.3}
                    name={type}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
